import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MessageCircle, MapPin, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { BRAND } from '@/lib/site';
import { toast } from 'sonner';

export default function Contact() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    intent: 'donate',
    message: '',
  });
  const [sending, setSending] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill all required fields.');
      return;
    }
    setSending(true);
    setTimeout(() => {
      setSending(false);
      toast.success("Message received. We'll respond within 1 business day.");
      setForm({ name: '', email: '', intent: 'donate', message: '' });
    }, 800);
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-parchment/30 dark:bg-[#1F1218] overflow-hidden"
    >
      {/* Decorative India outline backdrop */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 flex items-center justify-end opacity-[0.06] dark:opacity-[0.08]"
      >
        <svg viewBox="0 0 500 540" className="h-[120%] w-auto">
          <path
            d="M180 30 C200 28 230 30 260 38 C290 46 320 60 350 78 C380 96 410 120 420 150 C430 180 420 215 410 240 C400 265 390 290 400 320 C410 350 410 380 380 410 C350 440 310 470 270 480 C230 490 200 480 180 460 C160 440 140 410 130 380 C120 350 110 320 110 290 C110 260 105 230 100 200 C95 170 110 130 130 90 C150 60 160 35 180 30 Z"
            fill="#FF671F"
          />
        </svg>
      </div>

      <div className="container-x relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Left: contact details */}
          <div className="lg:col-span-5 space-y-7">
            <div>
              <p className="eyebrow">Reach Us</p>
              <h2 className="h-display mt-4 text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] text-maroon-700 dark:text-foreground">
                Let&rsquo;s build something
                <br />
                <span className="italic font-serif saffron-text">kind, together.</span>
              </h2>
              <p className="mt-5 text-foreground/70 max-w-md leading-relaxed">
                Operating Across India with active presence in{' '}
                <strong className="text-maroon-700 dark:text-saffron-200">
                  Delhi, Lucknow and Nagpur
                </strong>
                . Reach our team for donations, volunteering, CSR partnerships or media inquiries.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ContactTile
                icon={Phone}
                title="Call us"
                line={BRAND.phone}
                href={BRAND.phoneHref}
                tone="saffron"
                testid="contact-phone"
              />
              <ContactTile
                icon={MessageCircle}
                title="WhatsApp"
                line="Chat with our team"
                href={BRAND.whatsappHref}
                tone="emerald"
                testid="contact-whatsapp"
                external
              />
              <ContactTile
                icon={Mail}
                title="Email"
                line={BRAND.email}
                href={BRAND.emailHref}
                tone="maroon"
                testid="contact-email"
              />
              <ContactTile
                icon={MapPin}
                title="Operations"
                line="Across India"
                tone="gold"
                testid="contact-location"
              />
            </div>

            <div className="rounded-2xl border border-saffron-500/30 bg-saffron-50/60 dark:bg-saffron-500/5 p-5">
              <p className="font-serif text-base text-foreground/80">
                <span className="font-display text-lg font-semibold text-maroon-700 dark:text-saffron-200">
                  Working hours.
                </span>{' '}
                Mon–Sat · 10:00 am to 7:00 pm IST. Crisis response cell open 24×7 on WhatsApp.
              </p>
            </div>
          </div>

          {/* Right: form */}
          <motion.form
            data-testid="contact-form"
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 rounded-3xl glass-strong p-7 md:p-9"
          >
            <h3 className="h-display text-2xl md:text-3xl font-semibold text-maroon-700 dark:text-saffron-100">
              Send us a note
            </h3>
            <p className="mt-1 text-sm text-foreground/60">
              Choose the right channel and tell us how we can help.
            </p>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field id="c-name" label="Full name *">
                <Input
                  id="c-name"
                  data-testid="contact-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                  className="rounded-xl"
                />
              </Field>
              <Field id="c-email" label="Email *">
                <Input
                  id="c-email"
                  type="email"
                  data-testid="contact-email-input"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@email.com"
                  className="rounded-xl"
                />
              </Field>
            </div>

            <div className="mt-4">
              <Label className="text-xs uppercase tracking-wider text-foreground/60">
                I am writing about
              </Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {[
                  { v: 'donate', l: 'Donations' },
                  { v: 'volunteer', l: 'Volunteering' },
                  { v: 'csr', l: 'CSR Partnerships' },
                  { v: 'media', l: 'Press & Media' },
                  { v: 'other', l: 'Something else' },
                ].map((i) => {
                  const active = form.intent === i.v;
                  return (
                    <button
                      type="button"
                      key={i.v}
                      data-testid={`contact-intent-${i.v}`}
                      onClick={() => setForm({ ...form, intent: i.v })}
                      className={`rounded-full border px-3.5 py-1.5 text-sm transition-all ${
                        active
                          ? 'bg-maroon-700 border-maroon-700 text-white'
                          : 'border-border bg-card hover:border-maroon-500/40 text-foreground/80'
                      }`}
                    >
                      {i.l}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-4">
              <Field id="c-msg" label="Message *">
                <Textarea
                  id="c-msg"
                  rows={5}
                  data-testid="contact-message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Tell us a little about your inquiry…"
                  className="rounded-xl"
                />
              </Field>
            </div>

            <Button
              type="submit"
              size="lg"
              data-testid="contact-submit"
              disabled={sending}
              className="mt-6 w-full sm:w-auto rounded-full bg-saffron-500 hover:bg-saffron-600 px-8 py-6 text-base text-white shadow-[0_12px_28px_-12px_rgba(255,103,31,0.6)]"
            >
              <Send className="h-4 w-4 mr-2" />
              {sending ? 'Sending…' : 'Send Message'}
            </Button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function ContactTile({ icon: Icon, title, line, href, tone, testid, external }) {
  const tones = {
    saffron: 'bg-saffron-50 text-saffron-700 dark:bg-saffron-500/10 dark:text-saffron-300',
    maroon: 'bg-maroon-50 text-maroon-700 dark:bg-maroon-500/15 dark:text-saffron-200',
    emerald: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300',
    gold: 'bg-gold-100 text-gold-700 dark:bg-gold-400/10 dark:text-gold-300',
  };
  const Wrapper = href ? 'a' : 'div';
  const props = href
    ? { href, ...(external ? { target: '_blank', rel: 'noreferrer' } : {}) }
    : {};
  return (
    <Wrapper
      data-testid={testid}
      {...props}
      className="group flex items-center gap-3 rounded-2xl border border-border/70 bg-card p-4 hover:-translate-y-0.5 hover:shadow-md transition-all"
    >
      <div className={`grid h-11 w-11 flex-none place-items-center rounded-xl ${tones[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-xs uppercase tracking-wider text-foreground/55">{title}</p>
        <p className="font-semibold text-foreground group-hover:text-saffron-600 transition">
          {line}
        </p>
      </div>
    </Wrapper>
  );
}

function Field({ id, label, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs uppercase tracking-wider text-foreground/60">
        {label}
      </Label>
      {children}
    </div>
  );
}
